const refreshes = new Map();
// Start time of the latest refresh per key. Bursts of account-wide events
// coalesce into at most one refresh per window; the trailing refresh still
// starts after the last event, so the final state is always a fresh read.
const lastStarted = new Map();
export const REFRESH_WINDOW_MS = 1000;
let identityEpoch = 0;

export const getRefreshEpoch = () => identityEpoch;

export function clearRefreshContext() {
  identityEpoch += 1;
  for (const entry of refreshes.values()) {
    clearTimeout(entry.timer);
    entry.resolve();
  }
  refreshes.clear();
  lastStarted.clear();
}

const pruneStarts = now => {
  if (lastStarted.size < 500) return;
  for (const [key, startedAt] of lastStarted) {
    if (now - startedAt >= REFRESH_WINDOW_MS) lastStarted.delete(key);
  }
};

export function installPersonalReadContext(store) {
  store.watch(
    (_, getters) => JSON.stringify([
      getters.getCurrentAccountId, getters.getCurrentUserID,
    ]),
    clearRefreshContext,
    { immediate: true, flush: 'sync' }
  );
}

// enqueue is the existing per-conversation read/write serializer. Each trailing
// round rejoins its tail, allowing already queued explicit writes to run first.
export function requestRefresh(key, operation, enqueue) {
  const existing = refreshes.get(key);
  if (existing) {
    existing.operation = operation;
    if (existing.running) existing.dirty = true;
    return existing.promise;
  }
  const entry = { operation, running: false, dirty: false };
  entry.promise = new Promise((resolve, reject) => {
    entry.resolve = resolve;
    entry.reject = reject;
  });
  refreshes.set(key, entry);
  const current = () => refreshes.get(key) === entry;
  let schedule;
  const start = () => {
    entry.timer = null;
    enqueue(key, async () => {
      if (!current()) return;
      entry.running = true;
      const now = Date.now();
      pruneStarts(now);
      lastStarted.set(key, now);
      await entry.operation();
    }).then(() => {
      if (!current()) return;
      entry.running = false;
      if (entry.dirty) {
        entry.dirty = false;
        schedule();
      } else {
        refreshes.delete(key);
        entry.resolve();
      }
    }, error => {
      if (!current()) return;
      refreshes.delete(key);
      entry.reject(error);
    });
  };
  schedule = () => {
    const wait = (lastStarted.get(key) ?? -Infinity) + REFRESH_WINDOW_MS - Date.now();
    if (wait > 0) entry.timer = setTimeout(start, wait);
    else start();
  };
  schedule();
  return entry.promise;
}
